/**
 * Client for the blind dead drop that code-word linking rides on. The service
 * hands out numbered nameplates with three write-once slots and never sees
 * anything but opaque bytes; see projects/cloud/rendezvous.
 */

export type RendezvousSlot = "a" | "b" | "a2"

export interface Nameplate {
    nameplate: number
    holder: string
    expiresAt: string
}

export class RendezvousError extends Error {
    constructor(
        readonly code: string,
        message: string
    ) {
        super(message)
        this.name = "RendezvousError"
    }
}

export interface RendezvousTransport {
    allocate(ttlSeconds: number, signal?: AbortSignal): Promise<Nameplate>
    put(nameplate: number, slot: RendezvousSlot, payload: Uint8Array, holder: string | null, signal?: AbortSignal): Promise<void>
    /** Resolves with the slot bytes, or null when the poll budget ran out without a write. */
    get(nameplate: number, slot: RendezvousSlot, holder: string | null, wait: boolean, signal?: AbortSignal): Promise<Uint8Array | null>
    remove(nameplate: number, holder: string, signal?: AbortSignal): Promise<void>
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

async function readError(response: Response): Promise<RendezvousError> {
    try {
        const body: unknown = await response.json()
        if (isRecord(body) && typeof body.error === "string") {
            return new RendezvousError(body.error, typeof body.message === "string" ? body.message : body.error)
        }
    } catch {
        // Fall through to the status.
    }
    return new RendezvousError(`http_${response.status}`, `The link service answered ${response.status}`)
}

/** Fetch-backed transport against `${baseUrl}/link`. */
export function createRendezvousTransport(baseUrl: string, fetchImpl: typeof fetch = fetch): RendezvousTransport {
    const root = `${baseUrl.replace(/\/$/, "")}/link`
    const headers = (holder: string | null, extra: Record<string, string> = {}) => ({ ...(holder ? { "WildLark-Holder": holder } : {}), ...extra })
    return {
        async allocate(ttlSeconds, signal) {
            const response = await fetchImpl(`${root}/nameplates`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ttlSeconds }),
                cache: "no-store",
                ...(signal ? { signal } : {}),
            })
            if (!response.ok) throw await readError(response)
            const body: unknown = await response.json()
            if (!isRecord(body) || typeof body.nameplate !== "number" || typeof body.holder !== "string" || typeof body.expiresAt !== "string") {
                throw new RendezvousError("invalid_response", "The link service returned an invalid nameplate")
            }
            return { nameplate: body.nameplate, holder: body.holder, expiresAt: body.expiresAt }
        },
        async put(nameplate, slot, payload, holder, signal) {
            const response = await fetchImpl(`${root}/nameplates/${nameplate}/slots/${slot}`, {
                method: "PUT",
                headers: headers(holder, { "Content-Type": "application/octet-stream" }),
                // A fresh copy guarantees a plain ArrayBuffer-backed body for fetch.
                body: new Uint8Array(payload),
                cache: "no-store",
                ...(signal ? { signal } : {}),
            })
            if (!response.ok) throw await readError(response)
        },
        async get(nameplate, slot, holder, wait, signal) {
            const response = await fetchImpl(`${root}/nameplates/${nameplate}/slots/${slot}${wait ? "?wait=1" : ""}`, {
                method: "GET",
                headers: headers(holder),
                cache: "no-store",
                ...(signal ? { signal } : {}),
            })
            if (response.status === 204) return null
            if (!response.ok) throw await readError(response)
            return new Uint8Array(await response.arrayBuffer())
        },
        async remove(nameplate, holder, signal) {
            const response = await fetchImpl(`${root}/nameplates/${nameplate}`, {
                method: "DELETE",
                headers: headers(holder),
                cache: "no-store",
                ...(signal ? { signal } : {}),
            })
            if (!response.ok && response.status !== 404) throw await readError(response)
        },
    }
}

/** Polls a slot until it is written, the deadline passes, or the signal aborts. */
export async function awaitSlot(
    transport: RendezvousTransport,
    nameplate: number,
    slot: RendezvousSlot,
    holder: string | null,
    options: { deadline: number; signal?: AbortSignal; now?: () => number }
): Promise<Uint8Array> {
    const now = options.now ?? (() => Date.now())
    for (;;) {
        if (options.signal?.aborted) throw new RendezvousError("aborted", "Linking was cancelled")
        if (now() >= options.deadline) throw new RendezvousError("expired", "The code expired before the other side answered")
        const payload = await transport.get(nameplate, slot, holder, true, options.signal)
        if (payload) return payload
    }
}
